// One hook per list page: the filter tree, the saved views and the URL (`?view=` + `?f=`) kept in step.
// `snapshot()` / `restore(state, sort)` let the page save its own toggles (lane, sort, include-history…) inside a view.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { listSavedViews, createSavedView, updateSavedView, deleteSavedView, pinSavedView, duplicateSavedView } from "@/lib/api";
import { emptyTree, decodeTree, encodeTree, normaliseTree, sameTree, bareTree } from "./filterEngine";

const errText = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";

export function useListView({ page, region = "UK", snapshot, restore, urlPrefix = "" }) {
  const K_VIEW = `${urlPrefix}view`, K_F = `${urlPrefix}f`;   // two lists on one page keep their own URL keys
  // Emma, 21 Oct 2026 (ref 23e4b19e2a): the filter tree only lived in the URL, so a sidebar hop away and back wiped it.
  // Remembered per tab (sessionStorage, like the quick toggles) and restored whenever the URL arrives without ?f= / ?view=.
  const SS_KEY = `lv:${page}:${region}:${urlPrefix}`;
  const [params, setParams] = useSearchParams();
  const saved = useMemo(() => {
    if (params.get(K_F) || params.get(K_VIEW)) return null;      // the URL always wins when it carries filters
    try { return JSON.parse(sessionStorage.getItem(SS_KEY) || "null"); } catch { return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const restoredFromTab = !!(saved && (saved.f || saved.view));
  const [tree, setTreeRaw] = useState(() => decodeTree(params.get(K_F)) || (saved?.f ? decodeTree(saved.f) : null) || emptyTree());
  const [activeId, setActiveId] = useState(() => params.get(K_VIEW) || saved?.view || null);
  const [views, setViews] = useState([]);
  const [canPublish, setCanPublish] = useState(false);
  const [me, setMe] = useState("");
  const booted = useRef(false);
  const snapRef = useRef(snapshot); snapRef.current = snapshot;
  const restoreRef = useRef(restore); restoreRef.current = restore;

  const reloadViews = useCallback(async () => {
    try {
      const r = await listSavedViews(page, region);
      setViews(r.views || []); setCanPublish(!!r.can_publish); setMe(r.me || "");
      return r.views || [];
    } catch (e) { toast.error("Saved views: " + errText(e)); return []; }
  }, [page, region]);

  const syncUrl = useCallback((t, vid) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      const enc = encodeTree(t);
      if (enc) p.set(K_F, enc); else p.delete(K_F);
      if (vid) p.set(K_VIEW, vid); else p.delete(K_VIEW);
      return p;
    }, { replace: true });
  }, [setParams, K_F, K_VIEW]);

  // first load: ?view= wins, else the pinned default (unless the URL — or this tab's memory — already carries filters)
  useEffect(() => {
    (async () => {
      const vs = await reloadViews();
      if (booted.current) return;
      booted.current = true;
      if (restoredFromTab) { syncUrl(tree, activeId); return; }   // put the remembered filter back in the URL too
      const fromUrl = params.get(K_F), vid = params.get(K_VIEW);
      const v = vid ? vs.find((x) => x.view_id === vid) : (!fromUrl ? vs.find((x) => x.pinned) : null);
      if (!v) return;
      setActiveId(v.view_id);
      if (!fromUrl) { setTreeRaw(normaliseTree(v.filter)); restoreRef.current?.(v.state || {}, v.sort || null); }
      if (!vid) syncUrl(normaliseTree(v.filter), v.view_id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadViews]);

  // remember the filter + view for this tab so leaving the page and coming back keeps them
  useEffect(() => {
    try { sessionStorage.setItem(SS_KEY, JSON.stringify({ f: encodeTree(tree) || "", view: activeId || "" })); } catch { /* storage full / private mode */ }
  }, [SS_KEY, tree, activeId]);

  const setTree = useCallback((t) => { setTreeRaw(t); syncUrl(t, activeId); }, [syncUrl, activeId]);

  const applyView = useCallback((v) => {
    const t = normaliseTree(v.filter);
    setActiveId(v.view_id); setTreeRaw(t);
    restoreRef.current?.(v.state || {}, v.sort || null);
    syncUrl(t, v.view_id);
  }, [syncUrl]);

  const clearView = useCallback(() => {
    setActiveId(null); setTreeRaw(emptyTree());
    restoreRef.current?.(null, null);
    syncUrl(emptyTree(), null);
  }, [syncUrl]);

  const activeView = useMemo(() => views.find((v) => v.view_id === activeId) || null, [views, activeId]);
  const current = useCallback(() => {
    const s = snapRef.current?.() || {};
    const { sort = null, ...state } = s;
    return { filter: bareTree(tree), sort, state };
  }, [tree]);
  const dirty = useMemo(() => {
    if (!activeView) return false;
    const cur = current();
    // seeded / filter-only views carry no sort or state — only the filter counts as a change for them
    const sortDirty = activeView.sort != null && JSON.stringify(cur.sort) !== JSON.stringify(activeView.sort);
    const stateDirty = Object.keys(activeView.state || {}).length > 0 && JSON.stringify(cur.state) !== JSON.stringify(activeView.state);
    return !sameTree(tree, normaliseTree(activeView.filter)) || sortDirty || stateDirty;
  }, [activeView, tree, current]);

  const run = async (fn, ok) => {
    try { const r = await fn(); if (ok) toast.success(ok); await reloadViews(); return r; }
    catch (e) { toast.error(errText(e)); return null; }
  };
  const saveNew = async ({ name, shared = false }) => {
    const v = await run(() => createSavedView({ page, region, name, shared, ...current() }), `View “${name}” saved`);
    if (v) { setActiveId(v.view_id); syncUrl(tree, v.view_id); }
    return v;
  };
  const saveChanges = () => activeView && run(() => updateSavedView(activeView.view_id, current()), `“${activeView.name}” updated`);
  const rename = (v, name) => run(() => updateSavedView(v.view_id, { name }), "Renamed");
  const share = (v, shared) => run(() => updateSavedView(v.view_id, { shared }), shared ? "Shared with the team" : "Now personal");
  const pin = (v, pinned) => run(() => pinSavedView(v.view_id, pinned), pinned ? `“${v.name}” is now the default view` : "Default view unpinned");
  const duplicate = async (v) => { const c = await run(() => duplicateSavedView(v.view_id), "Duplicated"); if (c) applyView(c); };
  const remove = async (v) => { const r = await run(() => deleteSavedView(v.view_id), `“${v.name}” deleted`); if (r && v.view_id === activeId) clearView(); };

  return { tree, setTree, views, activeView, activeId, dirty, canPublish, me, applyView, clearView, saveNew, saveChanges, rename, share, pin, duplicate, remove, reloadViews };
}
